import type { HelpContent } from './types';

export const rolesRatesHelp: HelpContent = {
  pageTitle: 'Roles & Rates',
  description: 'View the bill rates and cost rates assigned to your resources, organised by role and department.',

  functionality: {
    overview: 'The Roles & Rates page lists all resources along with their bill and cost rates. Search, filter, and browse resources, then open any resource to see their rate details, project allocations, and working pattern.',

    keyFeatures: [
      {
        label: 'Resource rates overview',
        detail: 'See the bill rate and cost rate for every resource in your organisation in one place.',
      },
      {
        label: 'Search and filter',
        detail: 'Find resources quickly by name, role, or department using the search bar and filter controls.',
      },
      {
        label: 'Grid and list views',
        detail: 'Switch between a card grid view and a list view to suit how you prefer to browse.',
      },
      {
        label: 'Column chooser',
        detail: 'In list view, choose which columns are visible using the column configurator to tailor the information shown.',
      },
      {
        label: 'Resource detail panel',
        detail: 'Click any resource to open a detail panel showing their rates, project allocations, and working pattern.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'Search bar',
        description: 'Filter resources in real time by typing a name, role, or department.',
      },
      {
        name: 'Filter controls',
        description: 'Filter resources by department, status, or other fields to narrow down the list.',
      },
      {
        name: 'View toggle',
        description: 'Switch between the card grid view and the list view.',
      },
      {
        name: 'Column chooser',
        description: 'Available in list view. Choose which columns are displayed to tailor the information shown.',
      },
      {
        name: 'Resource card / row',
        description: 'Displays key information for each resource including name, role, department, bill rate, and cost rate. Click to open the full detail panel.',
      },
      {
        name: 'Resource detail panel — Rates tab',
        description: 'Displays the resource\'s bill rate and cost rate as both day and hour rates.',
      },
      {
        name: 'Resource detail panel — Projects tab',
        description: 'Lists the projects the resource is currently allocated to, including their role, allocation percentage, and project dates.',
      },
      {
        name: 'Resource detail panel — Working Pattern tab',
        description: 'Shows the resource\'s working pattern including working days, hours per day, total hours per week, and FTE.',
      },
    ],

    useCases: [
      'Reviewing bill and cost rates across your team.',
      'Checking the rate assigned to a specific resource before allocating them to a project.',
      'Comparing rates across roles and departments.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Roles & Rates page lists all resources along with their bill and cost rates. Use the search bar or filter controls to find a specific resource, then click their card or row to view their rate details.',
    steps: [
      {
        title: 'How do I find a resource?',
        items: [
          { text: 'Use the search bar to filter by name, role, or department.' },
          { text: 'Use the filter controls to narrow results by department or status.' },
          { text: 'Switch between grid and list view using the view toggle.' },
        ],
      },
      {
        title: 'How do I view a resource\'s rates?',
        items: [
          { text: 'Click any resource card or row to open the detail panel.' },
          { text: 'Go to the Rates tab to see the resource\'s bill and cost rates as day and hour rates.' },
        ],
      },
      {
        title: 'How do I customise the columns in list view?',
        items: [
          { text: 'Switch to list view using the view toggle.' },
          { text: 'Click the column chooser icon to open the column configurator.' },
          { text: 'Select or deselect columns to tailor the information shown.' },
        ],
      },
    ],
    proTips: [
      'Switch to list view and use the column chooser to display bill rate and cost rate side by side — useful for quickly comparing margins across your team.',
      'Use the filter controls to narrow down by department when reviewing rates for a specific team.',
    ],
  },

  faq: [
    {
      question: 'Can I edit rates from this page?',
      answer: 'Rate editing is managed centrally by an Administrator. If you need to update a resource\'s rate, contact your Administrator.',
    },
    {
      question: 'What is the difference between bill rate and cost rate?',
      answer: 'The bill rate is the rate charged to the client for the resource\'s time. The cost rate is the internal cost of the resource\'s time to your organisation.',
    },
    {
      question: 'Why can\'t I see rates for some resources?',
      answer: 'Visibility of rates may depend on your access permissions. Contact your Administrator if you believe you should have access to rates that are not visible.',
    },
  ],

  relatedPages: [
    { label: 'Resource Directory', path: '/directory' },
    { label: 'Skills', path: '/skills' },
  ],
};
